import { dateTimeColumnSettings, GestCard, ICity, iconColumnSettings, ICoordinates, IKeyValue, isAdminOrEditor, ISharedConfig, setDialog, setSnackbar } from '@shared/library';
import openGestConfirmationDialog from '@shared/library/dist/components/gest/GestDialog/GestConfirmationDialog/GestConfirmationDialog';
import { languages } from '@shared/library/dist/static/language';
import axios, { AxiosError } from 'axios';
import React, { JSX } from 'react';
import config from '../../../../config';
import importer from '../../../../helpers/importer';
import localProps from '../../../../helpers/localProps';
import { composeName, filterLogPhotos } from '../methods';
import { interventionColumns, WFMLeftGrid, WFMRightGrid } from '../static';
import './InterventionManagement.scss';
//#region ICONS
import { DeleteForeverRounded, FormatListBulletedAdd, NoPhotography, PendingActions, PhotoCamera, Search } from '@mui/icons-material';
import CollectionsRoundedIcon from '@mui/icons-material/CollectionsRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import PlanIcon from '@mui/icons-material/NoteAlt';
import PreviewRoundedIcon from '@mui/icons-material/PreviewRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
//#endregion
//#region COMPONENTS
import { GoogleMap } from '@react-google-maps/api';
import GestDataGridPro from '@shared/library/dist/components/gest/GestDataGrid/GestDataGridPro';
import GestAutocomplete from '@shared/library/dist/components/gest/GestInputs/autocomplete/GestAutocomplete';
import GestButton from '@shared/library/dist/components/gest/GestInputs/button/GestButton';
import GestSelect from '@shared/library/dist/components/gest/GestInputs/select/GestSelect';
import GestTextArea from '@shared/library/dist/components/gest/GestInputs/text area/GestTextArea';
import GestLoading from '@shared/library/dist/components/gest/GestLoading/GestLoading';
import GestMap from '@shared/library/dist/components/gest/GestMap/GestMap';
import GestTabs from '@shared/library/dist/components/gest/GestTabs/GestTabs';
import Title from '@shared/library/dist/components/gest/Title/Title';
import { CustomGallery } from '../../../_gest';
import { ImageData } from '../../../_gest/CustomGallery/CustomGallery';
import InterventionCreateForm from './InterventionCreateForm/InterventionCreateForm';
import InterventionEditForm, { IInterventionEditFormProps } from './InterventionEditForm/InterventionEditForm';
import InterventionExport from './InterventionExport/InterventionExport';
import PhotoEditor from './PhotoEditor/PhotoEditor';
//#endregion
//#region MODELS
import { GridColDef, GridRenderCellParams, GridTreeNodeWithRender } from '@mui/x-data-grid';
import { IGestSelectOption } from '@shared/library/dist/components/gest/GestInputs/select/GestSelectProps';
import { IGestTab } from '@shared/library/dist/components/gest/GestTabs/GestTabsProps';
import { LocalizedStrings } from 'react-localization';
import { LangKey } from '../../../../models/ui_data/Lang_model';
import { IIntervention } from '../_models/Intervention';
import { IPlan } from '../_models/Plan';
//#endregion
//CONTEXTS
// TODO: Resolve back to original db fields when editing

/** INTERFACES */
export interface OriginalPhoto {
    id: number;
    category: string;
    url: string;
    log_id: number;
}

export interface PhotoResponse {
    [key: string]: OriginalPhoto[];
}

interface Log {
    id: number;
    intervention_id: number;
    operator_id: number;
    log_id: number;
    lat?: number;
    lng?: number;
    alt?: number;
    old_reading: number;
    new_reading: number;
    new_digits?: number;

    seral_code?: string;
    status?: string;
    result?: string;
    annotations?: string;
    anomaly?: string;
    anomaly_notes?: string;
    new_brand?: string;
    new_serial?: string;
    valves?: string;
    address?: string;
    new_location?: string;

    intervention_start_date?: Date;
    old_reading_date?: Date;
    intervention_end_date?: Date;
    created_at: Date;
}

interface InterventionAnomaly {
    id: number;
    description: string;
}

export interface IWfmOptions {
    interventionTypes: string[],
    interventionStatuses: string[],
    meterLocations: string[],
    meterBrands: string[],
    interventionSpecifics: string[],
    archiveCategories: string[];
    meterValves: { code: string; label: string; }[],
    plans: IPlan[],
    areas: ICity[],
    operators: { id: number; fullname: string; }[],
}

type TViewModes = "plan" | "search" | "city";

//customizations
interface IInterventionManagementProps {
    customInterventionColumns?: (lang?: LocalizedStrings<any>) => GridColDef<IIntervention, number | string | Date>[];
    customInterventionEditForm?: (props: IInterventionEditFormProps) => JSX.Element;
    config: ISharedConfig;
};

const InterventionManagement = (props: IInterventionManagementProps) => {
    document.title = config.app.name;
    const lang = languages(props?.config.settings?.lang);

    //#region STATES
    const [options, setOptions] = React.useState<IWfmOptions>();
    const optionsRef = React.useRef<any>({
        interventionTypes: {},
        interventionStatuses: {},
        meterLocations: {},
        meterBrands: {},
        interventionSpecifics: {},
        meterValves: {},
        plans: {},
        areas: {},
        operators: {},
        archiveCategories: {}
    });
    //SEARCH
    const [searchValue, setSearchValue] = React.useState<string>();

    //INTERVENTIONS
    const [searchedInterventions, setSearchedInterventions] = React.useState<IIntervention[]>([]);
    const [cityInterventions, setCityInterventions] = React.useState<IIntervention[]>([]);
    const [planInterventions, setPlanInterventions] = React.useState<IIntervention[]>([]);
    const [visibleInterventions, setVisibleInterventions] = React.useState<IIntervention[]>([]);
    const [anomalies, setAnomalies] = React.useState<InterventionAnomaly[]>([]);

    //LOGS && PHOTOS
    const [selectedTab, setSelectedTab] = React.useState<IGestTab>();
    const [logs, setLogs] = React.useState<IIntervention[]>([]);
    const [selectedLogID, setSelectedLogID] = React.useState(-1);
    const [transformedPhotos, setTransformedPhotos] = React.useState<ImageData[][]>([]);
    const photosRef = React.useRef<PhotoResponse>(undefined);

    //CITIES
    const [selectedCity, setSelectedCity] = React.useState<IGestSelectOption<(ICoordinates[])[]>>();

    //PLANS
    const [selectedPlan, setSelectedPlan] = React.useState<IGestSelectOption<undefined>>();

    //MAP
    const [cityBounds, setCityBounds] = React.useState<(ICoordinates[])[]>([]);
    const [clickedIntervention, setClickedIntervention] = React.useState<IIntervention>();
    const [center, setCenter] = React.useState<ICoordinates>();

    const map = React.useRef<React.Ref<GoogleMap>>(undefined);

    //FLAGS    
    const [viewMode, setViewMode] = React.useState<TViewModes>("plan");
    const [interventionsLoading, setInterventionsLoading] = React.useState(true);
    const [interventionsSearchLoading, setInterventionsSearchLoading] = React.useState(false);
    const [photosLoading, setPhotosLoading] = React.useState(false);
    const [logsLoading, setLogsLoading] = React.useState(false);
    //#endregion

    //#region METHODS

    //#region API CALLS
    //#region GET
    const getSelectsOptions = () =>
        axios.get<{ data: IWfmOptions; }>(`${config.api.internal}/WFM/Options`, { withCredentials: true })
            .then(res => {
                const opts = res.data.data;
                setOptions(opts);
                //cities
                const storedCityID = Number(localProps.global.get('WFMinterventionManagementCity'));
                const city = opts.areas.find(v => v.id === storedCityID) ?? opts.areas[0];
                localProps.global.set('WFMinterventionManagementCity', city.id);
                setSelectedCity({ value: city.id, label: city.name, data: city.geoArea });
                //plans
                const storedPlanID = Number(localProps.global.get('WFMinterventionManagementPlan'));
                const plan = opts.plans.find(v => v.id === storedPlanID) ?? opts.plans[0];
                localProps.global.set('WFMinterventionManagementPlan', plan.id);
                setSelectedPlan({ value: plan.id, label: composeName(plan) });
                getInterventions(plan.id, undefined, opts.areas);

                //#region Formatting Options
                const interventionTypes = opts.interventionTypes.reduce<IKeyValue<string>>((acc, value) => {
                    acc[value] = value;
                    return acc;
                }, {});

                const interventionStatuses = opts.interventionStatuses.reduce<IKeyValue<string>>((acc, value) => {
                    acc[value] = value;
                    return acc;
                }, {});

                const meterLocations = opts.meterLocations.reduce<IKeyValue<string>>((acc, value) => {
                    acc[value] = value;
                    return acc;
                }, {});

                const meterBrands = opts.meterBrands.reduce<IKeyValue<string>>((acc, value) => {
                    acc[value] = value;
                    return acc;
                }, {});

                const interventionSpecifics = opts.interventionSpecifics.reduce<IKeyValue<string>>((acc, value) => {
                    acc[value] = value;
                    return acc;
                }, {});

                const meterValves = opts.meterValves.reduce<IKeyValue<{ code: string; label: string; }>>((acc, value) => {
                    acc[value.code] = value;
                    return acc;
                }, {});

                const plans = opts.plans.reduce<IKeyValue<IPlan>>((acc, value) => {
                    acc[value.id as number] = value;
                    return acc;
                }, {});

                const areas = opts.areas.reduce<IKeyValue<ICity>>((acc, value) => {
                    acc[value.id as number] = value;
                    return acc;
                }, {});

                const operators = opts.operators.reduce<IKeyValue<{ id: number; fullname: string; }>>((acc, value) => {
                    acc[value.id] = value;
                    return acc;
                }, {});

                const archiveCategories = opts.archiveCategories.reduce<IKeyValue<string>>((acc, value) => {
                    acc[value] = value;
                    return acc;
                }, {});
                //#endregion

                optionsRef.current = {
                    interventionTypes,
                    interventionStatuses,
                    meterLocations,
                    meterBrands,
                    interventionSpecifics,
                    meterValves,
                    plans,
                    areas,
                    operators,
                    archiveCategories
                };
            })
            .catch((err: AxiosError) => {
                console.error(err);
                setInterventionsLoading(false);
            });

    const getAnomalies = () =>
        axios.get<{ data: InterventionAnomaly[]; }>(`${config.api.internal}/WFM/Anomalies`, { withCredentials: true })
            .then(res => setAnomalies(res.data.data))
            .catch((err: AxiosError) => console.error(err));

    const getInterventions = async (ID?: string | number, vmode?: TViewModes, cityBounds?: ICity[]) => {
        vmode ??= viewMode;
        let interventions: IIntervention[] = [];

        if (vmode !== "search") {
            const params = vmode === "plan" ? { planID: ID ?? selectedPlan?.value as string } : { cityID: ID ?? selectedCity?.value as number };

            setInterventionsLoading(true);
            try {
                interventions = (await axios.get<IIntervention[]>(`${config.api.internal}/WFM/Interventions`, { withCredentials: true, params })).data;
            }
            catch (ex) {
                setSnackbar(props.config, { message: "Errore durante richiesta interventi al server ", severity: 'error' });
            }
            setInterventionsLoading(false);

            if (vmode === "plan") {
                setPlanInterventions(interventions);
            }
            else {
                setCityInterventions(interventions);
            }
        }
        else {
            interventions = searchedInterventions.length ? searchedInterventions
                : searchValue && searchValue.length >= 3 ? await searchInterventions() ?? []
                    : [];
        }

        setVisibleInterventions(interventions);
        updateInterventionsMap(interventions, cityBounds);
    };

    const searchInterventions = (event?: React.SubmitEvent<HTMLFormElement>) => {
        event?.preventDefault();

        if (searchValue) {
            setClickedIntervention(undefined);
            setInterventionsSearchLoading(true);

            return axios.get<IIntervention[]>(`${config.api.internal}/WFM/Interventions/Search/${searchValue}`, { withCredentials: true })
                .then(res => {
                    const searched = res.data;
                    setSearchedInterventions(searched);

                    if (event) {
                        setVisibleInterventions(searched);
                        updateInterventionsMap(searched);
                    }

                    return searched;
                })
                .catch((err: AxiosError) => {
                    console.error(err);
                    return undefined;
                })
                .finally(() => setInterventionsSearchLoading(false));
        }
    };

    const getInterventionLogs = (intervention: IIntervention) => {
        setLogsLoading(true);
        return axios.get<IIntervention[]>(`${config.api.internal}/WFM/Plan/${intervention.plan_id}/Intervention/${intervention.id}/Logs`, { withCredentials: true })
            .then(res => setLogs(res.data))
            .catch((err: AxiosError) => {
                console.error(err);
                setSnackbar(props.config, { message: "Errore durante richiesta al server per i log dell'intervento", severity: 'error' });
            })
            .finally(() => setLogsLoading(false));
    };

    const getInterventionPhotos = (intervention: IIntervention) => {
        setPhotosLoading(true);
        return axios.get<PhotoResponse>(`${config.api.internal}/WFM/Plan/${intervention.plan_id}/Intervention/${intervention.id}/Photos`, { withCredentials: true })
            .then(res => {
                photosRef.current = res.data;
                const logPhotos = filterLogPhotos(lang, selectedLogID, photosRef.current);
                setTransformedPhotos(logPhotos);
            })
            .catch((err: AxiosError) => {
                console.error('Error loading photos:', err.message);
                setSnackbar(props.config, { message: "Errore durante richiesta al server per le foto dell'intervento", severity: 'error' });
                setTransformedPhotos([]);
            })
            .finally(() => setPhotosLoading(false));
    };
    //#endregion

    //DELETE
    const deleteUnprocessedIntervention = (intervention: IIntervention) => {
        if (intervention.status === "unprocessed") {
            axios.delete(`${config.api.internal}/WFM/Intervention/${intervention.id}/Delete`, { withCredentials: true })
                .then(() => {
                    const rows = visibleInterventions.filter(i => i.id !== intervention.id);
                    setVisibleInterventions(rows);

                    setDialog(props.config, undefined);
                    setSnackbar(props.config, { message: lang.successfully_removed, severity: 'success' });
                })
                .catch((err: AxiosError<{ detail?: string; }>) => {
                    console.error(err);
                    setSnackbar(props.config, { message: "Errore durante cancellazione dell'intervento", severity: 'error' });
                });
        }
        else {
            setSnackbar(props.config, { message: "Errore di conversione form", severity: 'warning' });
        }
    };
    //#endregion

    const printIcon = (id: number | string, status: string) => {
        if (clickedIntervention && clickedIntervention.id === id) return importer.ic.require('markerSelected.svg');

        switch (status) {
            case 'suspended': {
                return importer.ic.require('markerBanana.png');
            }
            case 'processed': {
                return importer.ic.require('markerGreen.png');
            }
            case 'attempted_processing': {
                return importer.ic.require('markerRed.png');
            }
            case 'unprocessed': {
                return importer.ic.require('markerBlue.png');
            }
        }
    };

    const updateLogPhotos = (logID: number) => {
        setSelectedLogID(logID);
        const filteredPhotos = filterLogPhotos(lang, logID, photosRef.current);
        setTransformedPhotos(filteredPhotos);
    };

    const updateInterventionsMap = (interventions: IIntervention[], opts?: ICity[]) => {
        const cityBounds = (opts ?? options?.areas)
            ?.filter(a => a.geoArea && interventions.some(i => i.lat && i.lng && i.municipality === a.id))
            .flatMap(a => a.geoArea!) ?? [];
        setCityBounds(cityBounds);
        setCenter(interventions[0] as ICoordinates);
    };

    const updateClickedIntervention = (i: IIntervention) => {
        const intervention = i.id !== clickedIntervention?.id ? i : undefined;
        setClickedIntervention(intervention);

        if (intervention) {
            setCenter(i as ICoordinates);
            getInterventionLogs(intervention);
            getInterventionPhotos(intervention);
        }
    };

    const switchViewMode = (newMode?: "search" | "plan" | "city") => {
        const mode = newMode ?? (viewMode === "city" ? viewMode : "plan");
        setViewMode(mode);
    };

    const updateSearchValue = (newvalue?: string) => {
        setClickedIntervention(undefined);

        const value = newvalue ?? "";
        setSearchValue(value);
        //if no valid value => switch mode
        switchViewMode(value.length >= 3 ? "search" : undefined);

        const visibles = value.length >= 3 ? []
            : viewMode === "city" ? cityInterventions
                : planInterventions;
        setVisibleInterventions(visibles);
    };

    const openEditFormModal = (params: GridRenderCellParams<IIntervention, string | number | Date, string | number | Date, GridTreeNodeWithRender>) => {
        setDialog(props?.config, {
            label: lang.edit_intervention,
            content:
                <InterventionEditForm
                    config={props.config}
                    {...{
                        lang,
                        options,
                        anomalies,
                        intervention: params.row,
                        customInterventionEditForm: props?.customInterventionEditForm,
                        onSuccess: newRow => {
                            const index = visibleInterventions.findIndex(i => i.id === newRow.id);

                            let rows = structuredClone(visibleInterventions);
                            rows[index] = structuredClone(newRow);
                            setVisibleInterventions(structuredClone(rows));
                        }
                    }} />
        });
    };
    //#endregion

    //STATE CHANGE
    React.useEffect(() => {
        getSelectsOptions();
        getAnomalies();
    }, []);

    //#region RENDER
    const interventionsColumns = React.useMemo(() => {
        let defaultColumns = interventionColumns({
            lang
            , cities: props.config.cache?.cities.options
            , plans: options?.plans
            , excluded: ["intervention_id", "list_id", viewMode !== "city" ? "plan_id" : "municipality"]
        });

        if (isAdminOrEditor(props.config.session?.userData?.role)) {
            //enable actions
            defaultColumns = [
                {
                    ...iconColumnSettings(undefined, <EditRoundedIcon />, lang.edit, openEditFormModal),
                    filterable: false,
                    sortable: false,
                },
                {
                    ...iconColumnSettings(
                        "delete"
                        , params => params.row.status === "unprocessed" ? <DeleteForeverRounded color='error' /> : undefined, lang.delete
                        , async params => {
                            const confirmed = await openGestConfirmationDialog(
                                dialog => setDialog(props.config, dialog),
                                lang,
                                "L'operazione è irreversibile",
                                true,
                            );
                            if (confirmed) {
                                deleteUnprocessedIntervention(params.row);
                            }
                        }
                    ),
                    filterable: false,
                    sortable: false,
                },
                ...defaultColumns
            ];
        }
        //apply customizations
        const customColumns = props?.customInterventionColumns?.(lang);
        const parsedCustomData = props.config.session?.userData?.custom_data
            ? JSON.parse(props.config.session.userData.custom_data)
            : undefined;
        const configColumns = parsedCustomData
            ? Object
                .keys(parsedCustomData)
                .map<GridColDef<IIntervention>>(k => ({
                    field: k,
                    headerName: lang[k as LangKey],
                    valueGetter: (_, row) => row.custom_data ? JSON.parse(row.custom_data)[k] : undefined,
                    renderCell: params => params.row.custom_data ? JSON.parse(params.row.custom_data)[k] : undefined
                }))
            : [];
        const customAdditiveColumns = [
            ...(customColumns?.filter(cc => !configColumns.some(c => c.field !== cc.field) && !defaultColumns.some(c => cc.field === c.field)) ?? []),
            ...configColumns
        ];
        defaultColumns = defaultColumns.map(c => c = customColumns?.find(cc => cc.field === c.field) ?? c);

        return [...defaultColumns, ...customAdditiveColumns];
    }, [lang, options, visibleInterventions]);

    const logColumns = React.useMemo<GridColDef<IIntervention>[]>(() => [
        //VISIBLE
        iconColumnSettings(undefined, params => filterLogPhotos(lang, params.id as number, photosRef.current).length ? <CollectionsRoundedIcon /> : undefined, undefined, params => {
            updateLogPhotos(params.id as number);
            setSelectedTab(tabs[0]);
        }),
        {
            field: 'operators',
            headerName: lang.operator,
        },
        {
            field: 'status',
            headerName: lang.status,
            valueGetter: (_, row) => lang[row.status as LangKey],
            renderCell: params => lang[params.row.status as LangKey],
            cellClassName: params => params.row.status ?? "",
            width: 150,
            maxWidth: 150,
        },
        {
            field: 'result',
            headerName: lang.result,
        },
        {
            field: 'annotations',
            headerName: lang.notes,
        },
        {
            field: 'anomaly',
            headerName: lang.anomaly,
        },
        {
            field: 'anomaly_notes',
            headerName: lang.anomaly_notes,
        },
        {
            field: 'new_brand',
            headerName: lang.new_brand,
        },
        {
            field: 'new_serial',
            headerName: lang.new_serial,
        },
        {
            field: 'new_digits',
            headerName: lang.digits_number,
        },
        dateTimeColumnSettings('old_reading_date', lang)!,
        {
            field: 'old_reading',
            headerName: lang.old_reading,
        },
        {
            field: 'new_reading',
            headerName: lang.new_reading,
        },
        {
            field: 'seal_code',
            headerName: lang.seal_code,
        },
        {
            field: 'valves',
            headerName: lang.valve,
            valueGetter: (_, row) => lang[row.valves?.toString() as LangKey] ?? optionsRef.current.meterValves[row.valves?.toString() ?? ""]?.label,
            renderCell: params => lang[params.row.valves?.toString() as LangKey] ?? optionsRef.current.meterValves[params.row.valves?.toString() ?? ""]?.label,
        },
        {
            field: 'address',
            headerName: lang.address,
        },
        {
            field: 'new_location',
            headerName: lang.new_location,
        },
        dateTimeColumnSettings('intervention_start_date', lang),
        dateTimeColumnSettings('intervention_end_date', lang),
    ], [lang, selectedLogID]);

    const tabs: IGestTab[] = React.useMemo(() => [
        { label: "Gallery" },
        { label: "Logs" }
    ], []);
    //#endregion

    return <div id="interventionManagement">
        <Title class='title justify-content-between' size="xl">
            {lang.intervention_management}

            <GestButton
                icon={<DownloadRoundedIcon />}
                label='Esporta interventi'
                onclick={() => setDialog(props.config, { label: lang.exportation, content: <InterventionExport {...{ lang, config: props.config }} /> })}
            />
        </Title>

        <GestCard id="interventionsGrid"
            title={lang.interventions}
            actions={
                <div className='align-items-center'>
                    <GestButton
                        iconBtn
                        disabled={Boolean(searchValue && searchValue.length >= 3)}
                        tooltip={`Visualizza per ${viewMode !== "city" ? lang.towns : lang.plans}`}
                        icon={viewMode !== "city" ? <LocationCityIcon /> : <PlanIcon />}
                        onclick={() => {
                            const newMode = viewMode !== "city" ? "city" : "plan";
                            switchViewMode(newMode);
                            getInterventions(undefined, newMode);
                        }}
                    />

                    <GestAutocomplete
                        disabled={Boolean(searchValue && searchValue.length >= 3)}
                        label={viewMode !== "city" ? lang.plan : lang.municipality}
                        w={"15vw"}
                        value={(viewMode !== "city" ? selectedPlan : selectedCity) as IGestSelectOption<ICoordinates[][] | undefined>}
                        options={((viewMode !== "city" ? options?.plans : options?.areas) ?? []).map<IGestSelectOption<ICoordinates[][] | undefined>>(o => ({
                            value: o.id,
                            label: "geoArea" in o ? o.name : composeName(o),
                            data: "geoArea" in o ? o.geoArea : undefined
                        }))}
                        onchange={selected => {
                            if (viewMode === "city") {
                                setSelectedCity(selected[0] as IGestSelectOption<ICoordinates[][]>);
                                localProps.global.set('WFMinterventionManagementCity', selected[0].value);
                            }
                            else {
                                setSelectedPlan(selected[0] as IGestSelectOption<undefined>);
                                localProps.global.set('WFMinterventionManagementPlan', selected[0].value);
                            }

                            getInterventions(selected[0]?.value as number);
                        }}
                    />

                    <form
                        onSubmit={searchInterventions}
                        className="flex-row align-items-center"
                        style={{ width: "22vw" }}
                    >
                        <GestButton
                            iconBtn
                            disabled={!searchValue || searchValue.length < 3}
                            loading={interventionsSearchLoading}
                            submit
                            tooltip={lang.search}
                            icon={<Search />}
                        />

                        <GestTextArea
                            minLen={3}
                            name='searchValue'
                            label="Ricerca Interventi (MIN 3 caratteri, MAX 3000 interventi)"
                            fullw
                            onchange={updateSearchValue}
                        />
                    </form>

                    <GestButton
                        iconBtn
                        icon={<FormatListBulletedAdd />}
                        disabled={!selectedPlan}
                        tooltip="Crea nuovo intervento"
                        onclick={() => setDialog(props.config, {
                            label: `Nuovo intervento`,
                            content:
                                <InterventionCreateForm
                                    config={props.config}
                                    {...{
                                        lang,
                                        options,
                                        anomalies,
                                        selectedCity,
                                        selectedPlan,
                                        onSuccess: getInterventions
                                    }}
                                />
                        })}
                    />
                </div>
            }
        >
            <GestDataGridPro
                lang={lang}
                loading={interventionsSearchLoading || interventionsLoading}
                columns={interventionsColumns}
                rows={visibleInterventions.map<IIntervention>(i => ({
                    ...i,
                    [lang.plan]: optionsRef.current.plans[i.plan_id!]?.name ?? i.plan_id,
                    [lang.municipality]: optionsRef.current.areas[i.municipality]?.name ?? i.municipality
                }))}
                selectedRowIDs={clickedIntervention?.id}
                w={WFMLeftGrid}
                onRowClick={params => {
                    photosRef.current = undefined;
                    setLogs([]);
                    setTransformedPhotos([]);
                    setSelectedLogID(-1);
                    updateClickedIntervention(params.row);
                }}
                toolbar={[
                    {
                        icon: <RefreshRoundedIcon />,
                        tooltip: 'Ricarica interventi',
                        onclick: getInterventions,
                        disabled: interventionsLoading,
                    },
                ]}
            />
        </GestCard>

        <GestCard id='photoLogsTabs'>
            <GestTabs
                selectedTab={selectedTab}
                onTabChange={setSelectedTab}
                tabs={[
                    {
                        label: "Gallery",
                        icon: <PhotoCamera />,
                        loading: photosLoading,
                        panel:
                            <div className='fitContent flex-column'>
                                {clickedIntervention ? <>
                                    <div className='justify-content-between'>
                                        <div className='align-items-center'>
                                            <GestSelect
                                                required
                                                disabled={logsLoading}
                                                onchange={selected => updateLogPhotos(selected[0].value as number)}
                                                value={selectedLogID}
                                                options={[
                                                    {
                                                        value: -1,
                                                        label: lang.all_photos
                                                    },
                                                    ...logs.map<IGestSelectOption<undefined>>(l => ({
                                                        value: l.id,
                                                        label: `Log ID: ${l.id}`,
                                                        disabled: !filterLogPhotos(lang, l.id as number, photosRef.current)?.length
                                                    }))
                                                ]}
                                            />

                                            <GestButton
                                                iconBtn
                                                disabled={selectedLogID <= 0}
                                                icon={<PreviewRoundedIcon />}
                                                onclick={() => setSelectedTab(tabs[1])}
                                            />
                                        </div>

                                        <GestButton
                                            label={lang.edit}
                                            icon={<EditRoundedIcon />}
                                            onclick={() => setDialog(props?.config, {
                                                label: lang.photo,
                                                content:
                                                    <PhotoEditor {...{
                                                        config: props.config,
                                                        lang,
                                                        clickedIntervention,
                                                        setTransformedPhotos,
                                                        photos: photosRef,
                                                        logID: selectedLogID,
                                                        categories: options?.archiveCategories ?? []
                                                    }} />
                                            })}
                                        />
                                    </div>

                                    {photosLoading
                                        ? <GestLoading />
                                        : !transformedPhotos.length
                                            ? <div className='centered fullw' style={{ height: "calc(100% - 40px)" }}>
                                                {photosLoading
                                                    ? <GestLoading />
                                                    : <strong className='align-items-center'><NoPhotography />No photo</strong>
                                                }
                                            </div>
                                            : <CustomGallery images={transformedPhotos ?? []} slotsPerPage={4} />
                                    }
                                </> : <></>}
                            </div>,
                    },
                    {
                        label: "Logs",
                        icon: <PendingActions />,
                        loading: logsLoading,
                        panel:
                            <GestDataGridPro
                                columns={logColumns}
                                rows={logs}
                                selectedRowIDs={selectedLogID}
                                w={WFMRightGrid}
                            />
                    }
                ]}

            />
        </GestCard>

        <GestCard id="map">
            <GestMap
                style={{ width: WFMRightGrid }}
                ref={map.current}
                zoom={clickedIntervention ? 17 : 12}
                center={center || config.env.map.defaultCenter}
                markers={visibleInterventions.map(i => ({
                    id: i.id!,
                    model: i,
                    options: {
                        icon: printIcon(i.id!, i.status!),
                        position: i as ICoordinates,
                        onClick: () => updateClickedIntervention(i),
                        // opacity:selectedIntervention === null ? 1 : (selectedIntervention.id === marker.id ? 1 : .2) ,
                        zIndex: clickedIntervention && clickedIntervention.id === i.id ? 2 : 1,
                    }
                }))}
                polygons={{ areas: [{ paths: cityBounds }] }}
            />
        </GestCard>
    </div>;
};
export default React.memo(InterventionManagement);